import { useCallback, useEffect, useContext } from 'react';
import posthog from 'posthog-js';
import loggerService, { LoggerLevel } from '../log';
import { VITE_ENV } from 'config';
import TenantRepository from '../tenant/repository';
import { AuthContext } from 'identityAndAccess/infrastructure/AwsCognitoJwtContext';
import UserRepository from 'identityAndAccess/infrastructure/user/repository';
import { networkMetadataService } from '../network/services/NetworkMetadataService';
import { storageMetadataService } from '../storage/services/StorageMetadataService';
import { deriveIsInternal } from './analyticsIdentity';
import { AnalyticsEventName } from '../analytics/events';

/**
 * Base metadata automatically attached to all PostHog events.
 */
export type BaseMetadata = {
  tenant_id?: string;
  user_id?: string;
  asset_id?: string;
  asset_class_id?: string;
  timestamp_ms: number;
  network: {
    online: boolean;
    type?: string;
    effectiveType?: string;
    downlink?: number;
    rtt?: number;
    lastUpdated: number;
  };
  storage: {
    quota?: number;
    usage?: number;
    available?: number;
    lastUpdated: number;
  };
};

export function setPosthogCapture(enabled: boolean) {
  // Analytics is hard-disabled outside the US for GDPR/data-residency reasons.
  // Never opt in regardless of cookie consent when analytics is disabled for
  // this region. Feature flags are unaffected (they don't depend on capturing).
  if (enabled && VITE_ENV.posthog.analyticsEnabled) {
    posthog.opt_in_capturing();
    posthog.set_config({
      persistence: 'localStorage+cookie',
      disable_persistence: false,
    });
  } else {
    posthog.set_config({ disable_persistence: true });
    posthog.opt_out_capturing();
  }
  // Persistence mode change can leave a stale cached flag evaluation in
  // place; force a fresh fetch so flags never depend on cache lifetime.
  posthog.reloadFeatureFlags();
}

export function identifyUser({
  user_id,
  tenant_id,
  tp2_tenant_id,
  is_internal,
  has_all_tenant_access,
}: {
  user_id: string;
  tenant_id?: string;
  tp2_tenant_id?: number;
  is_internal?: boolean;
  has_all_tenant_access?: boolean;
}) {
  try {
    const personProps: Record<string, unknown> = {
      user_id,
      tenant_id,
      environment: VITE_ENV.env,
    };
    // Only write these when known. Shipping `undefined` would persist a
    // null-ish person property, and a filter on a null-ish value silently
    // drops the user — the exact failure this ticket removes.
    if (is_internal !== undefined) {
      personProps.is_internal = is_internal;
    }
    if (has_all_tenant_access !== undefined) {
      personProps.has_all_tenant_access = has_all_tenant_access;
    }
    posthog.identify(user_id, personProps);
    setTenantGroup(tenant_id, tp2_tenant_id);
    // Super properties registered here will stick to all events
    posthog.register({
      app_version: window.appVersion,
      platform: /Mobi|Android/i.test(navigator.userAgent) ? 'mobile' : 'desktop',
      device_type: navigator.userAgent,
    });
  } catch (error) {
    loggerService.log(LoggerLevel.ERROR, 'PostHog identify failed:', error);
  }
}

/**
 * Sets `is_internal` on the current person after identify.
 *
 * The ID token carries no email, so the session-restore path cannot derive
 * this at identify time. It arrives once the user record resolves; this fills
 * it in without a second identify call. Idempotent.
 */
export function setInternalUserProperty(is_internal: boolean) {
  try {
    posthog.setPersonProperties({ is_internal });
  } catch (error) {
    loggerService.log(LoggerLevel.ERROR, 'PostHog set person properties failed:', error);
  }
}

/**
 * Associates the current session with its tenant group.
 *
 * The group key is the TP3 tenant uuid, which is derived one-way from the TP2
 * tenant id (`deterministicTp3Id`), so the TP2 id can't be recovered from the
 * key downstream — it's attached as a group property to make usage joinable to
 * account data. Omitted rather than sent as null when unavailable, so no
 * `tp2_tenant_id = null` cohort is created to poison breakdowns.
 */
export function setTenantGroup(tenant_id: string | undefined, tp2_tenant_id?: number) {
  try {
    if (!tenant_id) return;
    // Analytics is hard-disabled outside the US (GDPR); never write group
    // properties there. Feature flags are unaffected.
    if (!VITE_ENV.posthog.analyticsEnabled) return;
    posthog.group('tenant', tenant_id, tp2_tenant_id !== undefined ? { tp2_tenant_id } : undefined);
  } catch (error) {
    loggerService.log(LoggerLevel.ERROR, 'PostHog set group failed:', error);
  }
}

export function resetPosthog() {
  try {
    posthog.reset();
  } catch (error) {
    loggerService.log(LoggerLevel.ERROR, 'PostHog reset failed:', error);
  }
}

/**
 * Captures an event outside React, for callers with no hook context — notably
 * mutation handlers that must still run after the component that started the
 * work has unmounted.
 *
 * Prefer `usePostHogEvents().trackEvent` in components: it fills in tenant_id
 * and user_id automatically, whereas here they must be supplied (or omitted).
 */
export function captureEvent(
  eventName: AnalyticsEventName,
  properties: Partial<BaseMetadata> & Record<string, unknown>
) {
  // Layer 3 guard: never capture analytics events in regions where analytics is
  // disabled (GDPR). Feature flags still work everywhere.
  if (!VITE_ENV.posthog.analyticsEnabled) return;

  try {
    posthog.capture(eventName, {
      timestamp_ms: Date.now(),
      network: networkMetadataService.getNetworkMetadata(),
      storage: storageMetadataService.getStorageMetadata(),
      ...properties,
    });
  } catch (error) {
    loggerService.log(LoggerLevel.ERROR, 'PostHog event tracking failed:', error);
  }
}

interface UsePostHogEventsProps {
  /**
   * Optional tenant_id override. If not provided, will be automatically fetched from TenantRepository.
   */
  tenant_id?: string;
  /**
   * Optional user_id override. If not provided, will be automatically fetched from UserRepository.
   */
  user_id?: string;
}

/**
 * Hook for tracking PostHog events with automatic tenant and user injection.
 * Automatically fetches tenant_id and user_id from repositories if not provided as props.
 *
 * `trackEvent` accepts only an `AnalyticsEventName` (see `common/infrastructure/analytics/events`),
 * so a hardcoded string or template expression will not compile.
 *
 * Session scope is left to PostHog's own `$session_id`, which is already on every
 * event. This hook deliberately does not add a `session_id` of its own: it used to
 * mint one uuid per hook instance, and with ~25 call sites a single visit emitted
 * ~25 different values, so nothing session-based could be measured from it.
 *
 * @param props - Optional overrides for tenant_id and user_id
 * @returns Object with a trackEvent function
 */
export const usePostHogEvents = ({
  tenant_id: tenantIdOverride,
  user_id: userIdOverride,
}: UsePostHogEventsProps = {}) => {
  // Automatically fetch tenant and user if not provided as overrides
  const { tenant } = TenantRepository.useCurrentTenant();

  const authContext = useContext(AuthContext);
  const isAuthenticated = authContext?.isAuthenticated ?? false;
  const currentUser = UserRepository.useCurrentUser(isAuthenticated);

  const tenant_id = tenantIdOverride ?? tenant?.id ?? undefined;
  const user_id = userIdOverride ?? currentUser?.id ?? undefined;

  // Backfill is_internal once the user record resolves. Session restore
  // identifies from the ID token, which carries no email, so this is the only
  // point where the property can be derived on that path. Idempotent, and it
  // also self-heals a login whose getUser() failed.
  const currentUserEmail = currentUser?.email;
  useEffect(() => {
    if (!isAuthenticated || !currentUserEmail) return;
    setInternalUserProperty(deriveIsInternal(currentUserEmail));
  }, [isAuthenticated, currentUserEmail]);

  const trackEvent = useCallback(
    <T extends Record<string, unknown>>(
      eventName: AnalyticsEventName,
      properties: Partial<BaseMetadata> & T
    ) => {
      // Layer 3 guard: never capture analytics events in regions where
      // analytics is disabled (GDPR). Feature flags still work everywhere.
      if (!VITE_ENV.posthog.analyticsEnabled) {
        return;
      }
      try {
        const baseMetadata: BaseMetadata = {
          tenant_id,
          user_id,
          timestamp_ms: Date.now(),
          network: networkMetadataService.getNetworkMetadata(),
          storage: storageMetadataService.getStorageMetadata(),
        };
        const fullProperties = { ...baseMetadata, ...properties };
        posthog.capture(eventName, fullProperties);
      } catch (error) {
        loggerService.log(LoggerLevel.ERROR, 'PostHog event tracking failed:', error);
      }
    },
    [tenant_id, user_id]
  );

  return {
    trackEvent,
  };
};
